import { applyDraftDueCap, Campaign, Collab, endOfDayInTimezone, goLiveKind, goLiveWindowFor, visitDeadline } from '@foodfluence/shared';
import { convertFirebaseTimestampToDate, getPostDueDate } from './formatUtils';
import { getEffectiveEndDate } from 'utils/collabUtils';
import { getBrowserTimezone } from 'utils/timezoneUtils';
import { getAcceptKeptDueDate, getPostByDeadline } from '../../utils/dateUtils';

export const getStartDate = (campaign?: Campaign): Date => {
  return campaign?.startDate ? convertFirebaseTimestampToDate(campaign.startDate) : new Date();
};

export const getEndDate = (campaign?: Campaign, collab?: Collab): Date | null => {
  const effectiveEndDate = collab && campaign ? getEffectiveEndDate(campaign, collab) : campaign?.endDate;
  const end = effectiveEndDate ? convertFirebaseTimestampToDate(effectiveEndDate) : null;
  // Go-live: no visit slots after the visit deadline (draft collabs: go-live start minus review
  // time; post collabs: go-live end), even when a per-collab end date runs later.
  const cap = visitDeadline(collab, campaign, getBrowserTimezone());
  if (!cap) return end;
  return end && end < cap ? end : cap;
};

/**
 * The deadline a visit on `visitDate` gives the creator, as the visit write stamps it:
 * - go-live post collabs: go-live end;
 * - go-live draft collabs: visit + turnaround, pulled back to the draft cap (go-live start minus
 *   review time) but never before visit + 1 day;
 * - otherwise visit + turnaround.
 * A brand-approved extension replaces it when later (getAcceptKeptDueDate), except on post collabs.
 * `turnaroundDays` includes any extension days.
 */
export const getVisitContentDue = (
  collab: Collab | null | undefined,
  campaign: Campaign | null | undefined,
  visitDate: Date,
  turnaroundDays: number,
  timezone: string
): Date => {
  const window = goLiveWindowFor(collab, campaign);
  const kind = window ? goLiveKind(campaign, collab) : null;
  if (kind === 'post') return getPostByDeadline(collab, campaign, timezone) ?? getPostDueDate(visitDate, turnaroundDays, timezone);
  let due = getPostDueDate(visitDate, turnaroundDays, timezone);
  if (kind === 'draft') {
    // The picker date is UTC midnight of the store-local visit day; the end of that day in the
    // store timezone is an instant on it, which is what applyDraftDueCap counts from. Its uncapped
    // result is the same day as `due` (23:59 vs 10:00), so the earlier one is the capped date.
    const visitDay = endOfDayInTimezone(visitDate.toISOString().slice(0, 10), timezone);
    const capped = applyDraftDueCap(visitDay, turnaroundDays, 0, { contentGoLiveStart: window!.start, contentGoLiveEnd: window!.end, contentDeadlineKind: 'draft' }, timezone);
    if (capped < due) due = capped;
  }
  return getAcceptKeptDueDate(collab, campaign, timezone, due) ?? due;
};
